/**
 * Policy Service
 * Requirement 47 [MVP]: Policy and Governance Engine
 */

import axios from 'axios';
import { getActiveTenantId } from './tenantContext';

export type PolicyMode = 'preventive' | 'detective' | 'corrective';
export type PolicySeverity = 'low' | 'medium' | 'high' | 'critical';

export interface Policy {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  mode: PolicyMode;
  severity: PolicySeverity;
  enabled: boolean;
  conditions: string;
  promotion_stage: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePolicyParams {
  name: string;
  description?: string;
  mode?: PolicyMode;
  severity?: PolicySeverity;
  enabled?: boolean;
  conditions: string;
  promotion_stage?: string;
}

export interface PolicyMatch {
  resource_id: string;
  provider: string;
  service: string;
  region: string;
  account: string;
  monthly_cost: number;
  tags: Record<string, string>;
}

export interface DryRunResult {
  policy_id: string;
  policy_name: string;
  matched_count: number;
  matches: PolicyMatch[];
}

export interface EvaluationResult extends DryRunResult {
  new_violations: number;
  resolved_violations: number;
}

export interface PolicyViolation {
  id: string;
  policy_id: string;
  resource_id: string;
  service: string | null;
  provider: string | null;
  status: 'open' | 'waived' | 'resolved';
  waiver_reason: string | null;
  waiver_expiry: string | null;
  detected_at: string;
  resolved_at: string | null;
}

// tenantContextMiddleware requires a verified Bearer token on every
// cost-api route - tenant headers alone are no longer sufficient.
function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    'x-user-id': 'user-123',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const costApiUrl = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

export const policyService = {
  async listPolicies(): Promise<Policy[]> {
    const response = await axios.get(`${costApiUrl}/policies`, { headers: authHeaders() });
    return response.data.policies;
  },

  async createPolicy(params: CreatePolicyParams): Promise<Policy> {
    const response = await axios.post(`${costApiUrl}/policies`, params, { headers: authHeaders() });
    return response.data.policy;
  },

  async updatePolicy(id: string, params: Partial<CreatePolicyParams>): Promise<Policy> {
    const response = await axios.put(`${costApiUrl}/policies/${id}`, params, { headers: authHeaders() });
    return response.data.policy;
  },

  async deletePolicy(id: string): Promise<void> {
    await axios.delete(`${costApiUrl}/policies/${id}`, { headers: authHeaders() });
  },

  async dryRunPolicy(id: string): Promise<DryRunResult> {
    const response = await axios.post(`${costApiUrl}/policies/${id}/dry-run`, {}, { headers: authHeaders() });
    return response.data;
  },

  async evaluatePolicy(id: string): Promise<EvaluationResult> {
    const response = await axios.post(`${costApiUrl}/policies/${id}/evaluate`, {}, { headers: authHeaders() });
    return response.data;
  },

  async listViolations(status?: string): Promise<PolicyViolation[]> {
    const response = await axios.get(`${costApiUrl}/policies/violations`, {
      headers: authHeaders(),
      params: status ? { status } : {},
    });
    return response.data.violations;
  },

  async waiveViolation(violationId: string, reason: string, expiryDays?: number): Promise<PolicyViolation> {
    const response = await axios.put(
      `${costApiUrl}/policies/violations/${violationId}/waive`,
      { reason, expiryDays },
      { headers: authHeaders() }
    );
    return response.data.violation;
  },
};
